import { Link } from 'gatsby';
import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { useDataLayerPush } from '../../hooks/useDataLayerPush';
import NoPhoto from '../../images/no-photo.png';
import { Kfh_DevelopmentResalePropertyType } from '../../types/graphqlTypes';
import { currency } from '../../utils';
import { getDevelopmentSummary } from '../../utils/getDevelopmentSummary';
import { getUrlParts } from '../../utils/getUrlParts';
import { Button } from '../Button';
import { Gallery } from '../Gallery';
import { Img } from '../Img';
import { Typography } from '../Typography';
import { getCleanPropertyDetails } from '../../utils/getCleanPropertyDetails';

interface DevelopmentListPropertyCardProps {
  property: Kfh_DevelopmentResalePropertyType;
  as?: 'div' | 'li';
  vertical?: boolean;
  disableGallerySwipeOnMobile?: boolean;
  onWhiteBg?: boolean;
}

interface StyledDevelopmentListPropertyCardProps {
  vertical?: boolean;
  $onWhiteBg?: boolean;
  singleImage?: boolean;
}

const StyledDevelopmentListPropertyCard = styled.div<StyledDevelopmentListPropertyCardProps>`
  ${({ theme: { space, borders, colors, media, global }, vertical, singleImage }) => css`
    background-color: ${colors.white.default};
    border-radius: ${global.borderRadius.default};
    overflow: hidden;
    isolation: isolate;
    transform: translateZ(0);

    .gatsby-image-wrapper {
      height: 100%;
      width: 100%;
      background-color: ${colors.grey1.default};
    }

    .swiper-slide {
      img {
        object-fit: cover;
        height: 100%;
        width: 100%;
        aspect-ratio: 4/3;
        @media (min-width: ${media.lg}) {
          aspect-ratio: 191/180;
        }
      }
    }

    ${vertical &&
    css`
      display: flex;
      flex-direction: column;
    `};

    ${!vertical &&
    css`
      @media (min-width: ${media.lg}) {
        display: grid;
        grid-template-columns: 1fr 470px;
      }

      @media (min-width: ${media.xl}) {
        display: grid;
        grid-template-columns: ${singleImage ? '360px 1fr' : '576px 1fr'};
      }
    `};
  `};
`;

const StyledBody = styled.div<StyledDevelopmentListPropertyCardProps>`
  ${({ theme: { space, media }, vertical, $onWhiteBg }) => css`
    padding: ${vertical ? `${space.md} 0` : space.md};
    position: relative;

    ${vertical &&
    css`
      flex-grow: 1;
    `};

    ${$onWhiteBg &&
    css`
      padding-right: 0;
      padding-left: 0;
    `};

    @media (min-width: ${media.md}) {
      padding: ${vertical ? `${space.md}` : space.lg};
      display: flex;
      flex-direction: column;

      ${$onWhiteBg &&
      css`
        padding-right: 0;
        padding-left: 0;
      `};
    }
  `};
`;

const StyledAddress = styled(Typography)`
  ${({ theme: { space, media } }) => css`
    margin-top: ${space.sm};

    @media (min-width: ${media.md}) {
      margin-top: ${space.md};
    }
  `};
`;

const StyledDescription = styled(Typography)`
  ${({ theme: { space, maxMedia } }) => css`
    margin-top: ${space.md};
    margin-bottom: ${space.md};

    @media (max-width: ${maxMedia.md}) {
      display: none;
    }
  `};
`;

const StyledActions = styled.div`
  ${({ theme: { space, media } }) => css`
    display: none;

    @media (min-width: ${media.lg}) {
      display: flex;
      gap: ${space.sm};
      margin-top: auto;
    }
  `};
`;

const StyledAddressLink = styled(Link)`
  ${({ theme: { space, maxMedia } }) => css`
    text-decoration: none;

    @media (max-width: ${maxMedia.lg}) {
      &:after {
        content: '';
        position: absolute;
        z-index: 1;
        top: 0;
        left: 0;
        right: 0;
        height: 100%;
        background: rgba(255, 255, 255, 0);
      }
    }
  `};
`;

export const DevelopmentListPropertyCard: FC<DevelopmentListPropertyCardProps> = ({
  property,

  as,
  vertical,
  disableGallerySwipeOnMobile,
  onWhiteBg,
}) => {
  const { dataLayerPush } = useDataLayerPush();

  // Link state (passed through to property details for back link)

  const linkState = { prevPageType: 'property-listing' };

  const { address, developmentName, developmentMaxPrice, developmentMinPrice, plots, images } = property;
  const { summaryText } = getDevelopmentSummary(plots);

  // GA property click

  const propertyClick = {
    event: 'propertyClick',
    clickText: 'View development',
    clickBladeID: false,
    propertyClickDetails: getCleanPropertyDetails(property),
    ...getUrlParts(property.relativeUrl),
  };

  return (
    <>
      <StyledDevelopmentListPropertyCard as={as} vertical={vertical}>
        {/* Gallery */}
        <Gallery
          property={property}
          propertyCard
          vertical={vertical}
          disableGallerySwipeOnMobile={disableGallerySwipeOnMobile}
        >
          {images?.developmentImages?.length >= 1 ? (
            images?.developmentImages.map((image, index) => (
              <Link
                to={property.relativeUrl}
                state={linkState}
                key={`card-img-${index}`}
                onClick={() =>
                  dataLayerPush({
                    clickType: 'gallery image',
                    ...propertyClick,
                  })
                }
              >
                <Img
                  src={image}
                  alt={image.caption}
                  loading={index > 2 ? 'lazy' : undefined}
                  options={{
                    width: 382,
                    height: 360,
                    layout: 'constrained',
                    cropFocus: 'center',
                    placeholder: 'none',
                    quality: 80,
                    backgroundColor: '#F5F5F5',
                  }}
                  size="430x320"
                />
              </Link>
            ))
          ) : (
            <Link
              to={property.relativeUrl}
              state={linkState}
              onClick={() =>
                dataLayerPush({
                  clickType: 'gallery image',
                  ...propertyClick,
                })
              }
            >
              <Img src={NoPhoto} alt="" />
            </Link>
          )}
        </Gallery>

        {/* Body */}

        <StyledBody vertical={vertical} $onWhiteBg={onWhiteBg}>
          <Typography component="p" variant="titleMedium">
            {currency(developmentMinPrice) === currency(developmentMaxPrice) ? (
              <>{currency(developmentMinPrice)}</>
            ) : (
              <>
                {currency(developmentMinPrice)} - {currency(developmentMaxPrice)}
              </>
            )}
          </Typography>
          {/* Address */}

          <StyledAddress component="h2" variant="titleSmall">
            <StyledAddressLink
              to={property.relativeUrl}
              state={linkState}
              onClick={() =>
                dataLayerPush({
                  clickType: 'address title',
                  ...propertyClick,
                })
              }
            >
              {developmentName}
            </StyledAddressLink>
          </StyledAddress>

          {/* Description */}

          <StyledDescription component="p" variant="bodySmall">
            {summaryText && (
              <>
                {summaryText}
                <br />
              </>
            )}
            {address.town} {address.outwardCode}
          </StyledDescription>

          {/* Actions */}

          {!vertical && (
            <StyledActions>
              <Button
                url={property.relativeUrl}
                variant="solid"
                state={linkState}
                disableClickTracking
                onClick={() =>
                  dataLayerPush({
                    clickType: 'cta button',
                    ...propertyClick,
                  })
                }
              >
                View development
              </Button>
            </StyledActions>
          )}
        </StyledBody>
      </StyledDevelopmentListPropertyCard>
    </>
  );
};
